'use client'

import { marked } from 'marked';
import React, { useMemo } from 'react';
import { isChunkText, LlmChunkItem } from '../../api/types';

export type LlmChunkProps = {
  item: LlmChunkItem;
};

export const LlmChunk: React.FC<LlmChunkProps> = ({
  item,
}: LlmChunkProps) => {
  switch (item.type) {
    case 'string':
    case 'text': {
      const text = (isChunkText(item) ? item.text : item.content) || '';
      return (
        <LlmChunkText text={text} />
      );
    }
    case 'file': {
      const title = item.url.split('/').pop();
      return item.url ? (
        <div className="llm__row">
          <div className="llm__col --end-xs-12 --xs-10 --sm-9 --md-8 --lg-7 --xl-6">
            <a href={item.url} download={title} target="_blank" rel="noreferrer">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-paperclip-icon lucide-paperclip"><path d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551" /></svg>
              <span>{title}</span>
            </a>
          </div>
        </div>
      ) : (
        <code>{JSON.stringify(item)}</code>
      );
    }
    case 'image': {
      return item.url ? (
        <div className="llm__row">
          <div className="llm__col --end-xs-12 --xs-10 --sm-9 --md-8 --lg-7 --xl-6">
            <img src={item.url} alt="" />
          </div>
        </div>
      ) : (
        <code>{JSON.stringify(item)}</code>
      );
    }
    case 'image_url': {
      return item.image_url ? (
        <div className="llm__row">
          <div className="llm__col --end-xs-12 --xs-10 --sm-9 --md-8 --lg-7 --xl-6">
            <img src={item.image_url.url} alt="" />
          </div>
        </div>
      ) : (
        <code>{JSON.stringify(item)}</code>
      );
    }
    default:
      return <LlmChunkCard item={item} />;
  }
};

export function LlmChunkText({
  text,
}: {
  text: string;
}) {
  const html = useMemo(() => {
    if (!text) {
      console.error('LlmChunkText.error', text);
      return '';
    }
    return marked.parse(text);
  }, [text]);
  return (
    <p className="llm__text" dangerouslySetInnerHTML={{ __html: html }}></p>
  );
}

const USE_CARD = true;

export function LlmChunkCard({
  item,
}: LlmChunkProps) {
  const knownKeys = ['type', 'id', 'title', 'abstract', 'description', 'image', 'place', 'date', 'time', 'url'];
  const keys = useMemo(() => {
    const keys = Object.keys(item);
    return keys.sort((a, b) => {
      return knownKeys.indexOf(a) - knownKeys.indexOf(b);
    });
  }, []);
  return USE_CARD ? (
    <div className="llm__card">
      {keys.map((key) => {
        const value = String(item[key as keyof typeof item]);
        if (!value) {
          return undefined;
        }
        switch (key) {
          case 'type':
          case 'id':
          case 'place':
          case 'date':
          case 'time':
            return (
              <span key={key} className={`llm__card-${key} llm__pill`} title={key}>{value}</span>
            );
          case 'image':
            return (
              <div key={key} className={`llm__card-${key}`} title={key}>
                <a href={value} target="_blank" rel="noreferrer">
                  <img src={value} />
                </a>
              </div>
            );
          case 'url':
          case 'link':
          case 'href':
            return (
              <div key={key} className={`llm__card-${key}`} title={key}>
                <a className="llm__pill" href={value} target="_blank" title={value} rel="noreferrer">{value}</a>
              </div>
            );
          default:
            return (
              <div key={key} className={`llm__card-${key}`} title={key} dangerouslySetInnerHTML={{ __html: value }} />
            );
        }
      })}
    </div>
  ) : (
    <code>{JSON.stringify(item, null, 2)}</code>
  );
}
